<template>
    <div class="pm-page">
  
      <!-- ============ HERO / BANNIÈRE ============ -->
      <header class="pm-hero">
        <picture>
          <source
            srcset="../../public/img/theme/velocover.jpg 800w, ../../public/img/theme/velocover.jpg 1600w"
            type="image/webp"
          />
          <img
            src="../../public/img/theme/velocover.jpg"
            alt="Vélo publicitaire équipé d'un panneau d'affichage circulant dans une rue de Cotonou"
            class="pm-hero__bg"
            width="1600"
            height="900"
            fetchpriority="high"
          />
        </picture>
  
        <div class="pm-hero__panel" role="presentation">
          <span class="pm-hero__bolt pm-hero__bolt--tl" aria-hidden="true"></span>
          <span class="pm-hero__bolt pm-hero__bolt--tr" aria-hidden="true"></span>
          <span class="pm-hero__bolt pm-hero__bolt--bl" aria-hidden="true"></span>
          <span class="pm-hero__bolt pm-hero__bolt--br" aria-hidden="true"></span>
  
          <p class="pm-hero__eyebrow">PANNEAU MOBILE</p>
          <h1 class="pm-hero__title">Le vélo publicitaire</h1>
          <p class="pm-hero__subtitle">
            Votre marque circule au plus près des piétons, sans bruit, sans
            carburant, dans les rues et marchés que les véhicules n'atteignent
            pas. Le support le plus économique et le plus agile de notre offre.
          </p>
          <a href="#comment-ca-marche" class="pm-btn pm-btn--primary">Voir comment ça marche</a>
        </div>
      </header>
  
      <main class="pm-main">
  
        <!-- ============ PRÉSENTATION ============ -->
        <section class="pm-intro" aria-labelledby="intro-title">
          <h2 id="intro-title" class="pm-section-title">Qu'est-ce que le vélo publicitaire&nbsp;?</h2>
          <p class="pm-intro__text">
            Un vélo équipé d'un panneau d'affichage fixé à l'arrière ou sur les
            côtés, conduit par un agent qui parcourt un itinéraire défini à
            travers quartiers, marchés et zones piétonnes. Contrairement aux
            panneaux fixes, il va chercher l'audience là où elle se trouve,
            plusieurs fois par jour si besoin.
          </p>
        </section>
  
        <!-- ============ AVANTAGES ============ -->
        <section class="pm-supports" aria-labelledby="avantages-title">
          <h2 id="avantages-title" class="pm-section-title">Les avantages du vélo publicitaire</h2>
  
          <div class="pm-grid">
            <article
              v-for="(avantage, index) in avantages"
              :key="index"
              class="pm-card pm-card--simple"
            >
              <div class="pm-card__body">
                <h3 class="pm-card__title">{{ avantage.title }}</h3>
                <p class="pm-card__desc">{{ avantage.text }}</p>
              </div>
            </article>
          </div>
        </section>
  
        <!-- ============ COMMENT ÇA MARCHE ============ -->
        <section id="comment-ca-marche" class="pm-steps" aria-labelledby="steps-title">
          <h2 id="steps-title" class="pm-section-title">Comment ça marche</h2>
  
          <ol class="pm-steps__list">
            <li
              v-for="step in etapes"
              :key="step.number"
              class="pm-steps__item"
            >
              <span class="pm-steps__number">{{ step.number }}</span>
              <div class="pm-steps__content">
                <h3 class="pm-steps__title">{{ step.title }}</h3>
                <p class="pm-steps__text">{{ step.text }}</p>
              </div>
            </li>
          </ol>
        </section>
  
        <!-- ============ FICHE TECHNIQUE ============ -->
        <section class="pm-spec-sheet" aria-labelledby="specs-title" itemscope itemtype="https://schema.org/Product">
          <h2 id="specs-title" class="pm-section-title">Fiche technique</h2>
          <meta itemprop="name" content="Vélo publicitaire" />
          <meta itemprop="brand" content="Panneau Mobile" />
          <meta itemprop="category" content="Publicité extérieure" />
  
          <div class="pm-spec-sheet__media">
            <img
              src="../../public/img/theme/steeve.jpg"
              alt="Vue rapprochée du panneau d'affichage fixé sur un vélo publicitaire"
              width="640"
              height="420"
              loading="lazy"
              itemprop="image"
            />
          </div>
  
          <dl class="pm-specs pm-specs--large">
            <div v-for="spec in specs" :key="spec.label" class="pm-specs__row">
              <dt>{{ spec.label }}</dt>
              <dd>{{ spec.value }}</dd>
            </div>
          </dl>
        </section>
  
        <!-- ============ CAS D'USAGE ============ -->
        <section class="pm-usecases" aria-labelledby="usecases-title">
          <h2 id="usecases-title" class="pm-section-title">Pour quels usages&nbsp;?</h2>
          <ul class="pm-usecases__list">
            <li v-for="(cas, index) in casUsage" :key="index">{{ cas }}</li>
          </ul>
        </section>
  
        <!-- ============ FAQ ============ -->
        <section class="pm-faq" aria-labelledby="faq-title">
          <h2 id="faq-title" class="pm-section-title">Questions fréquentes</h2>
          <div v-for="(item, index) in faq" :key="index" class="pm-faq__item">
            <h3 class="pm-faq__question">{{ item.question }}</h3>
            <p class="pm-faq__answer">{{ item.answer }}</p>
          </div>
        </section>
  
        <!-- ============ CTA FINAL ============ -->
        <section class="pm-cta">
          <h2 class="pm-section-title pm-section-title--light">
            Lancez votre campagne à vélo
          </h2>
          <p class="pm-cta__text">
            Indiquez votre quartier cible et la durée souhaitée&nbsp;: nous vous
            envoyons un devis sous 24h.
          </p>
          <a href="/contact" class="pm-btn pm-btn--primary">
            Demander un devis gratuit
          </a>
        </section>
  
      </main>
    </div>
  </template>
  
  <script>
  export default {
    name: 'VeloPublicitairePage',
  
    data() {
      return {
        avantages: [
          {
            title: 'Coût d\'entrée le plus bas',
            text: "Pas de production grand format ni de location de véhicule motorisé : c'est le support le plus accessible pour tester une campagne locale.",
          },
          {
            title: 'Accès aux zones piétonnes',
            text: "Marchés, ruelles, abords d'écoles, zones non carrossables : le vélo passe partout où les véhicules publicitaires ne peuvent pas circuler.",
          },
          {
            title: '100% écologique et silencieux',
            text: "Aucune émission, aucun bruit de moteur. Un argument que vous pouvez aussi communiquer à votre propre audience.",
          },
          {
            title: 'Proximité avec le public',
            text: "Le conducteur peut s'arrêter, distribuer des flyers ou échantillons, et engager la conversation : un contact direct impossible avec un panneau fixe.",
          },
          {
            title: "Flexibilité d'itinéraire",
            text: "Le parcours peut être ajusté en cours de campagne selon les zones qui répondent le mieux, sans frais de réinstallation.",
          },
          {
            title: 'Déploiement rapide',
            text: "Pas de démarche d'installation ni d'autorisation lourde : une campagne peut démarrer en quelques jours.",
          },
        ],
  
        etapes: [
          {
            number: '01',
            title: 'Vous définissez votre zone et votre message',
            text: "Quartiers ciblés, horaires de circulation, visuel à afficher : nous cadrons la campagne avec vous.",
          },
          {
            number: '02',
            title: 'Nous produisons le panneau',
            text: "Impression et fixation du panneau publicitaire sur le vélo, à vos couleurs.",
          },
          {
            number: '03',
            title: 'Le vélo circule selon l\'itinéraire prévu',
            text: "Un agent parcourt les rues, marchés et axes définis, aux horaires convenus, pendant toute la durée de la campagne.",
          },
          {
            number: '04',
            title: 'Vous recevez un suivi de diffusion',
            text: "Photos et points de passage vous sont transmis pour confirmer la bonne exécution de la campagne.",
          },
        ],
  
        specs: [
          { label: 'Format du panneau', value: '0,82 m × 1,15 m (sur mesure possible)' },
          { label: 'Position', value: 'Arrière et/ou latéral' },
          { label: 'Durée minimale', value: '6 heures' },
          { label: 'Zone de circulation', value: 'Quartier(s) au choix, Cotonou et autres villes' },
          { label: 'Horaires types', value: 'Entre 7h et 18h jour - 18h et 23h nuit' },
          { label: 'Visibilité', value: '+5000 personnes touchées par support par jour' },
          { label: 'Option distribution', value: 'Flyers ou échantillons par le conducteur' },
        ],
  
        casUsage: [
          "Lancement de produit ou d'une offre",
          'Promotion d\'un commerce ou d\'un événement local',
          'Campagne à petit budget pour tester un message avant un format plus large',
          'Complément d\'une campagne façade pour une couverture précise',
        ],
  
        faq: [
          {
            question: 'Quel budget prévoir pour une campagne en vélo publicitaire ?',
            answer:
              "C'est l'un des supports les plus économique de notre offre. Le tarif dépend de la durée et du nombre de vélos déployés : contactez-nous pour un devis précis selon votre zone et votre budget.",
          },
          {
            question: 'Le vélo publicitaire fonctionne-t-il aussi bien qu\'un vehicule panneau mobile classique ?',
            answer:
              "Il répond à un objectif différent : le panneau mobile sur véhicule couvre de grands axes rapidement, tandis que le vélo cible des zones précises et piétonnes avec un contact plus direct.",
          },
          {
            question: 'Peut-on choisir précisément les rues parcourues ?',
            answer:
              "Oui. L'itinéraire est défini avec vous avant le lancement et peut être ajusté en cours de campagne selon les retours terrain.",
          },
        ],
      };
    },
  
    metaInfo() {
      const baseUrl = 'https://www.panneaumobile.bj';
      const canonical = `${baseUrl}/velo-publicitaire`;
      const title = 'Vélo Publicitaire au Bénin | Affichage Mobile de Proximité — Panneau Mobile';
      const description =
        "Le vélo publicitaire : un support mobile, économique et écologique pour toucher les piétons et marchés de Cotonou. Avantages, fonctionnement, fiche technique et devis gratuit.";
      const ogImage = `${baseUrl}/images/hero/velo-publicitaire-cotonou-1600.jpg`;
  
      return {
        title,
        titleTemplate: null,
        htmlAttrs: { lang: 'fr' },
        meta: [
          { charset: 'utf-8' },
          { name: 'description', content: description, vmid: 'description' },
          {
            name: 'keywords',
            content:
              'vélo publicitaire Bénin, vélo publicitaire Cotonou, affichage mobile écologique, publicité de proximité, panneau mobile vélo',
            vmid: 'keywords',
          },
          { name: 'robots', content: 'index, follow', vmid: 'robots' },
  
          { property: 'og:type', content: 'website', vmid: 'og:type' },
          { property: 'og:title', content: title, vmid: 'og:title' },
          { property: 'og:description', content: description, vmid: 'og:description' },
          { property: 'og:url', content: canonical, vmid: 'og:url' },
          { property: 'og:image', content: ogImage, vmid: 'og:image' },
          { property: 'og:locale', content: 'fr_BJ', vmid: 'og:locale' },
          { property: 'og:site_name', content: 'Panneau Mobile', vmid: 'og:site_name' },
  
          { name: 'twitter:card', content: 'summary_large_image', vmid: 'twitter:card' },
          { name: 'twitter:title', content: title, vmid: 'twitter:title' },
          { name: 'twitter:description', content: description, vmid: 'twitter:description' },
          { name: 'twitter:image', content: ogImage, vmid: 'twitter:image' },
        ],
        link: [{ rel: 'canonical', href: canonical, vmid: 'canonical' }],
        script: [
          {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Supports publicitaires',
                  item: `${baseUrl}/supports-publicitaires`,
                },
                { '@type': 'ListItem', position: 3, name: 'Vélo publicitaire', item: canonical },
              ],
            },
            vmid: 'ld-breadcrumb',
          },
          {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: 'Vélo publicitaire',
              description,
              image: `${baseUrl}/images/supports/velo-publicitaire-detail.jpg`,
              brand: { '@type': 'Brand', name: 'Panneau Mobile' },
              category: 'Publicité extérieure',
              url: canonical,
            },
            vmid: 'ld-product',
          },
          {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'HowTo',
              name: 'Comment fonctionne une campagne de vélo publicitaire',
              step: this.etapes.map((s) => ({
                '@type': 'HowToStep',
                name: s.title,
                text: s.text,
              })),
            },
            vmid: 'ld-howto',
          },
          {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: this.faq.map((f) => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
            vmid: 'ld-faq',
          },
        ],
      };
    },
  };
  </script>
  
  <style scoped>
  /* ===================================================
     TOKENS
  =================================================== */
  .pm-page {
    --pm-ink: #11131a;
    --pm-paper: #f5efe3;
    --pm-amber: #ffd600;
    --pm-vermilion: #d94f30;
    --pm-teal: #1f6f66;
    --pm-steel: #8b93a1;
  
    --pm-font-display: 'Anton', 'Archivo Black', sans-serif;
    --pm-font-body: 'Poppins', sans-serif;
    --pm-font-mono: 'Poppins', sans-serif;
  
    background: var(--pm-paper);
    color: var(--pm-ink);
    font-family: var(--pm-font-body);
    overflow-x: hidden;
  }
  
  /* ===================================================
     HERO
  =================================================== */
  .pm-hero {
    position: relative;
    min-height: 80vh;
    display: flex;
    align-items: center;
    background: var(--pm-ink);
    overflow: hidden;
  }
  
  .pm-hero__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.38;
    filter: saturate(0.9) contrast(1.05);
  }
  
  .pm-hero__panel {
    position: relative;
    z-index: 1;
    margin: 4rem auto 4rem 6vw;
    max-width: 600px;
    background: var(--pm-ink);
    border: 3px solid var(--pm-teal);
    padding: clamp(2rem, 4vw, 3.5rem);
    transform: rotate(-1.2deg);
    box-shadow: 14px 14px 0 rgba(31, 111, 102, 0.22);
  }
  
  .pm-hero__bolt {
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--pm-steel);
    box-shadow: inset 0 0 0 2px var(--pm-ink), inset 0 0 0 3px var(--pm-steel);
  }
  .pm-hero__bolt--tl { top: -7px; left: -7px; }
  .pm-hero__bolt--tr { top: -7px; right: -7px; }
  .pm-hero__bolt--bl { bottom: -7px; left: -7px; }
  .pm-hero__bolt--br { bottom: -7px; right: -7px; }
  
  .pm-hero__eyebrow {
    font-family: var(--pm-font-mono);
    letter-spacing: 0.18em;
    font-size: 0.75rem;
    color: var(--pm-teal);
    margin: 0 0 1rem;
  }
  
  .pm-hero__title {
    font-family: var(--pm-font-display);
    font-weight: 400;
    text-transform: uppercase;
    color: var(--pm-paper);
    font-size: clamp(2.1rem, 5vw, 3.6rem);
    line-height: 1.02;
    margin: 0 0 1.25rem;
  }
  
  .pm-hero__subtitle {
    color: #cfd2db;
    font-size: 1.05rem;
    line-height: 1.6;
    margin: 0 0 2rem;
    max-width: 50ch;
  }
  
  /* ===================================================
     BUTTONS
  =================================================== */
  .pm-btn {
    display: inline-block;
    font-family: 'Poppins';
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.85rem;
    text-decoration: none;
    padding: 0.85rem 1.6rem;
    border: 2px solid currentColor;
    transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
    border-radius: 5px;
    font-weight: 600;
  }
  .pm-btn--primary {
    color: var(--pm-ink);
    background: var(--pm-amber);
    border-color: var(--pm-amber);
  }
  .pm-btn--primary:hover { transform: translateY(-2px); }
  
  /* ===================================================
     SECTIONS GENERIC
  =================================================== */
  .pm-main { max-width: 1180px; margin: 0 auto; padding: 0 6vw; }
  
  .pm-section-title {
    font-family: var(--pm-font-display);
    text-transform: uppercase;
    font-weight: 400;
    font-size: clamp(1.6rem, 3vw, 2.3rem);
    margin: 0 0 1.5rem;
  }
  .pm-section-title--light { color: var(--pm-paper); }
  
  .pm-intro { padding: 5rem 0 2rem; max-width: 65ch; }
  .pm-intro__text { font-size: 1.05rem; line-height: 1.7; color: #383b44; }
  
  /* ===================================================
     AVANTAGES (cards)
  =================================================== */
  .pm-supports { padding: 2rem 0 4rem; }
  
  .pm-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 1.75rem;
  }
  
  .pm-card {
    position: relative;
    background: #fffdf8;
    display: flex;
    border-radius: 10px;
    flex-direction: column;
    box-shadow: 0px 0px 3px 0px #0000005c;;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .pm-card:hover {
    border: 0.5px solid #c4c4c4;
  }
  .pm-card--simple { min-height: auto; }
  
  .pm-card__bolt {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--pm-steel);
    z-index: 2;
  }
  .pm-card__bolt--tl { top: -5px; left: -5px; }
  .pm-card__bolt--br { bottom: -5px; right: -5px; }
  
  .pm-card__body { padding: 1.5rem; }
  
  .pm-card__title {
    font-family: var(--pm-font-display);
    text-transform: uppercase;
    font-weight: 400;
    font-size: 1.1rem;
    margin: 0 0 0.6rem;
    color: var(--pm-vermilion);
  }
  .pm-card__desc {
    font-size: 0.92rem;
    line-height: 1.6;
    color: #383b44;
    margin: 0;
  }
  
  /* ===================================================
     COMMENT ÇA MARCHE (étapes — séquence réelle)
  =================================================== */
  .pm-steps { padding: 2rem 0 4rem; }
  
  .pm-steps__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0;
    border-top: 2px solid var(--pm-ink);
  }
  
  .pm-steps__item {
    display: grid;
    grid-template-columns: 4.5rem 1fr;
    gap: 1.5rem;
    align-items: start;
    padding: 1.75rem 0;
    border-bottom: 2px solid var(--pm-ink);
  }
  
  .pm-steps__number {
    font-family: var(--pm-font-display);
    font-size: 2.2rem;
    color: var(--pm-amber);
    line-height: 1;
  }
  
  .pm-steps__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.4rem;
  }
  .pm-steps__text {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #383b44;
    margin: 0;
    max-width: 60ch;
  }
  
  /* ===================================================
     FICHE TECHNIQUE
  =================================================== */
  .pm-spec-sheet {
    padding: 2rem 0 4rem;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) 1.2fr;
    gap: 2.5rem;
    align-items: start;
  }
  
  .pm-spec-sheet__media {
    /* border: 2px solid var(--pm-ink); */
    overflow: hidden;
    aspect-ratio: 4 / 3;
  }
  .pm-spec-sheet__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
  
  .pm-specs { margin: 0; border-top: 1px dashed var(--pm-steel); grid-column: 2; }
  .pm-specs--large .pm-specs__row { padding: 0.7rem 0; font-size: 0.88rem; }
  .pm-specs__row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    border-bottom: 1px dashed #d8d2c4;
    font-family: var(--pm-font-mono);
    font-size: 0.78rem;
  }
  .pm-specs__row dt { color: var(--pm-steel); margin: 0; }
  .pm-specs__row dd { margin: 0; text-align: right; font-weight: 600; }
  
  @media (max-width: 760px) {
    .pm-spec-sheet { grid-template-columns: 1fr; }
    .pm-specs { grid-column: 1; }
  }
  
  /* ===================================================
     CAS D'USAGE
  =================================================== */
  .pm-usecases { padding: 0 0 4rem; max-width: 70ch; }
  .pm-usecases__list { margin: 0; padding-left: 1.2rem; }
  .pm-usecases__list li {
    font-size: 0.98rem;
    line-height: 1.7;
    color: #383b44;
    margin-bottom: 0.5rem;
  }
  
  /* ===================================================
     FAQ
  =================================================== */
  .pm-faq { padding: 1rem 0 4rem; max-width: 70ch; }
  .pm-faq__item { padding: 1.25rem 0; border-bottom: 1px solid #d8d2c4; }
  .pm-faq__question { font-size: 1.05rem; margin: 0 0 0.5rem; }
  .pm-faq__answer { font-size: 0.95rem; line-height: 1.6; color: #383b44; margin: 0; }
  
  /* ===================================================
     CTA FINAL
  =================================================== */
  .pm-cta {
    background: var(--pm-ink);
    margin: 0 -6vw;
    padding: 4.5rem 6vw;
    text-align: center;
  }
  .pm-cta__text {
    color: #cfd2db;
    max-width: 50ch;
    margin: 0 auto 2rem;
    line-height: 1.6;
  }
  
  /* ===================================================
     RESPONSIVE / ACCESSIBILITY
  =================================================== */
  @media (max-width: 640px) {
    .pm-hero__panel { margin: 3rem 4vw; transform: rotate(0); }
    .pm-steps__item { grid-template-columns: 1fr; }
  }
  
  @media (prefers-reduced-motion: reduce) {
    .pm-card, .pm-btn { transition: none; }
  }
  
  .pm-btn:focus-visible,
  a:focus-visible {
    outline: 3px solid var(--pm-teal);
    outline-offset: 2px;
  }
  </style>